import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Input,
  Label,
  ButtonGroup,
  Col,
  Badge,
} from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import Swal from "sweetalert2";
import axios from "axios";
import useFullPageLoader from "../../../hooks/useFullPageLoader";
import moment from "moment";

// Reporte por Articulo de los costos finales.
// - Junta los pagos por contenedor (igual que ReporteContenedor) y saca el costo por pza de cada contenedor.
// - Cruza con los articulos que llegaron en cada embarque (ArticulosEmbarques) y su cantidad.
// - Propone un costoMX nuevo por articulo = promedio ponderado del costo de entrada contra el inventario actual.
// - Permite marcar cuales costear y actualiza el costoMX (endpoint articulosEditarCostoMX).
function ReporteCostosArticulo() {
  const { user } = useContext(AuthContext);
  const URL_PAGOS_PROVEEDORES = process.env.REACT_APP_URL_PAGOS_PROVEEDORES;
  const URL_ANTICIPOS_PROVEEDORES =
    process.env.REACT_APP_URL_ANTICIPOS_PROVEEDORES;
  const URL_ARTICULOS_EMBARQUES = process.env.REACT_APP_URL_ARTICULOS_EMBARQUES;
  const URL_INVENTARIOS = process.env.REACT_APP_URL_INVENTARIOS;
  const URL_ARTICULOS = process.env.REACT_APP_URL_ARTICULOS;
  const URL_LOGS = process.env.REACT_APP_URL_LOGS;

  let hoy = new Date();
  let inicio = new Date();
  inicio.setDate(inicio.getDate() - 30);
  let endDate = moment(hoy).format("YYYY-MM-DD");
  let startDate = moment(inicio).format("YYYY-MM-DD");

  const [selectedFechaInicio, setSelectedFechaInicio] = useState(startDate);
  const [selectedFechaFin, setSelectedFechaFin] = useState(endDate);
  // % adicional por contenedor (gastos/overhead), editable, default 7% (igual que ReporteContenedor)
  const [porcentaje, setPorcentaje] = useState(7);
  const [search, setSearch] = useState("");

  // datos por embarque: { [idEmbarques]: { contenedor, pzs, rows: [{concepto, estado, totalMXN}] } }
  const [embarquesData, setEmbarquesData] = useState({});
  // seleccion de contenedores que SÍ se promedian: { [contenedor]: bool }
  const [contenedoresSel, setContenedoresSel] = useState({});
  // articulosEmbarques: [{ idArticulo, idEmbarques, cantidad }]
  const [articulosEmb, setArticulosEmb] = useState([]);
  // inventario actual por articulo: { idArticulo: { existencia, costoMX, SKU, nombre } }
  const [inventario, setInventario] = useState({});
  // seleccion para costear
  const [seleccionados, setSeleccionados] = useState({});
  const [validaBoton, setValidaBoton] = useState(true);

  const [loader, showLoader, hideLoader] = useFullPageLoader();

  const monedaMXN = (n) =>
    new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(Number(n) || 0);

  // Carga pagos + anticipos y arma el costo por pza de cada embarque/contenedor
  useEffect(() => {
    if (!selectedFechaInicio || !selectedFechaFin) return;
    showLoader();
    const headers = {
      Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
    };

    const pPagos = axios
      .get(
        `${URL_PAGOS_PROVEEDORES}Fechas/${selectedFechaInicio}/${selectedFechaFin}`,
        { headers }
      )
      .then((response) =>
        (response.data || [])
          .filter((a) => a.is_active !== "No")
          .filter((a) => a.embarques && a.embarques.idEmbarques)
          .map((a) => ({
            idEmbarques: a.embarques.idEmbarques,
            contenedor:
              a.embarques.contenedor || `EMB #${a.embarques.idEmbarques}`,
            pzs: Number(a.embarques.total_cantidad) || 0,
            concepto: a.conceptoNombre || a.conceptosPago?.concepto || "PAGO",
            estado: a.estado || "Simulado",
            totalMXN: Number(a.totalMXN) || 0,
          }))
      )
      .catch(() => []);

    const pAnticipos = axios
      .get(
        `${URL_ANTICIPOS_PROVEEDORES}Fechas/${selectedFechaInicio}/${selectedFechaFin}`,
        { headers }
      )
      .then((response) =>
        (response.data || [])
          .filter((a) => a.is_active === "Si" && Number(a.importe) > 0)
          .filter((a) => a.embarques && a.embarques.idEmbarques)
          .map((a) => {
            const importe = Number(a.importe) || 0;
            const tipoCambio = Number(a.tipoCambio) || 0;
            const montoPesos =
              Number(a.montoPesos) > 0 ? Number(a.montoPesos) : importe * tipoCambio;
            return {
              idEmbarques: a.embarques.idEmbarques,
              contenedor:
                a.embarques.contenedor || `EMB #${a.embarques.idEmbarques}`,
              pzs: Number(a.embarques.total_cantidad) || 0,
              concepto: "ANTICIPO",
              estado: "Pagado", // los anticipos son reales
              totalMXN: montoPesos,
            };
          })
      )
      .catch(() => []);

    Promise.all([pPagos, pAnticipos])
      .then(([pagos, anticipos]) => {
        const todos = [...pagos, ...anticipos];
        // agrupa por idEmbarques guardando los renglones (para prioridad real>simulado)
        const map = {};
        todos.forEach((r) => {
          if (!map[r.idEmbarques]) {
            map[r.idEmbarques] = { contenedor: r.contenedor, pzs: 0, rows: [] };
          }
          if ((r.pzs || 0) > (map[r.idEmbarques].pzs || 0)) {
            map[r.idEmbarques].pzs = r.pzs || 0;
          }
          if (!map[r.idEmbarques].contenedor && r.contenedor) {
            map[r.idEmbarques].contenedor = r.contenedor;
          }
          map[r.idEmbarques].rows.push({
            concepto: r.concepto,
            estado: r.estado,
            totalMXN: r.totalMXN,
          });
        });
        setEmbarquesData(map);
        // inicializa la selección de contenedores (todos marcados por defecto)
        const conts = [
          ...new Set(
            Object.values(map)
              .map((e) => e.contenedor)
              .filter(Boolean)
          ),
        ].sort();
        setContenedoresSel((prev) => {
          const next = {};
          conts.forEach((c) => {
            next[c] = prev[c] !== undefined ? prev[c] : true;
          });
          return next;
        });
        hideLoader();
      })
      .catch(() => hideLoader());
  }, [selectedFechaInicio, selectedFechaFin]);

  // Carga los articulos por embarque (todos activos) y el inventario acumulado (una vez)
  useEffect(() => {
    const headers = {
      Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
    };
    axios
      .get(URL_ARTICULOS_EMBARQUES, { headers })
      .then((response) => {
        const rows = (response.data || [])
          .filter((a) => a.is_active === "Si")
          .map((a) => ({
            idArticulo: a.articulos?.[0]?._id || "",
            SKU: a.articulos?.[0]?.SKU || "",
            nombre: a.articulos?.[0]?.nombre || "",
            idEmbarques: a.embarques?.[0]?.idEmbarques || "",
            cantidad: Number(a.cantidad) || 0,
          }))
          .filter((a) => a.idArticulo && a.idEmbarques && a.cantidad > 0);
        setArticulosEmb(rows);
      })
      .catch((err) => console.log(err));

    axios
      .get(`${URL_INVENTARIOS}Acumulado`, { headers })
      .then((response) => {
        const inv = {};
        (response.data || []).forEach((a) => {
          const id = a.idArticulo;
          if (!id) return;
          inv[id] = {
            existencia: Number(a.cantidad) || 0,
            costoMX: Number(a.costoMX) || 0,
            SKU: a.SKU || a.codigo || "",
            nombre: a.nombre || "",
          };
        });
        setInventario(inv);
      })
      .catch((err) => console.log(err));
  }, []);

  // Regla de control: el costo real (Pagado) tiene prioridad; el simulado
  // solo se usa si no hay un pago real para ese mismo concepto.
  const efectivas = (rows) => {
    const hayReal = {};
    rows.forEach((r) => {
      if (r.estado === "Pagado") hayReal[r.concepto] = true;
    });
    return rows.filter((r) => (hayReal[r.concepto] ? r.estado === "Pagado" : true));
  };

  // Reporte por articulo
  const reporte = useMemo(() => {
    const pct = Number(porcentaje) || 0;
    // costo por pza de cada embarque (solo contenedores seleccionados), con % adicional
    const costoPza = {};
    Object.keys(embarquesData).forEach((idEmb) => {
      const e = embarquesData[idEmb];
      if (!contenedoresSel[e.contenedor]) return; // no seleccionado -> no se promedia
      const rows = efectivas(e.rows);
      const subtotal = rows.reduce((a, r) => a + (Number(r.totalMXN) || 0), 0);
      const totalConAdicional = subtotal * (1 + pct / 100);
      costoPza[idEmb] = e.pzs > 0 ? totalConAdicional / e.pzs : 0;
    });

    // agrupa articulosEmbarques por articulo, ponderando el costo de entrada por cantidad
    const map = {};
    articulosEmb.forEach((r) => {
      const cpp = costoPza[r.idEmbarques];
      if (cpp === undefined) return; // ese embarque no tiene pagos en el periodo
      if (!map[r.idArticulo]) {
        map[r.idArticulo] = {
          idArticulo: r.idArticulo,
          SKU: r.SKU,
          nombre: r.nombre,
          cantidadNueva: 0,
          sumaPonderada: 0,
        };
      }
      map[r.idArticulo].cantidadNueva += r.cantidad;
      map[r.idArticulo].sumaPonderada += r.cantidad * cpp;
    });

    const arr = Object.values(map).map((a) => {
      const inv = inventario[a.idArticulo] || { existencia: 0, costoMX: 0 };
      const existencia = Number(inv.existencia) || 0;
      const costoMXActual = Number(inv.costoMX) || 0;
      // Costo de entrada = promedio ponderado de los lotes recibidos
      const costoEntrada =
        a.cantidadNueva > 0 ? a.sumaPonderada / a.cantidadNueva : 0;
      // Nuevo costo = promedio ponderado contra lo que hay en inventario
      const denom = existencia + a.cantidadNueva;
      const costoMXNuevo =
        denom > 0
          ? (existencia * costoMXActual + a.cantidadNueva * costoEntrada) / denom
          : costoEntrada;
      return {
        idArticulo: a.idArticulo,
        SKU: a.SKU || inv.SKU || "",
        nombre: a.nombre || inv.nombre || "",
        existencia,
        costoMXActual,
        cantidadNueva: a.cantidadNueva,
        costoEntrada,
        costoMXNuevo,
      };
    });

    arr.sort((a, b) => (a.SKU > b.SKU ? 1 : -1));
    return arr;
  }, [embarquesData, contenedoresSel, articulosEmb, inventario, porcentaje]);

  // Lista de contenedores para el multi-select
  const listaContenedores = useMemo(
    () =>
      [
        ...new Set(
          Object.values(embarquesData)
            .map((e) => e.contenedor)
            .filter(Boolean)
        ),
      ].sort(),
    [embarquesData]
  );

  const toggleContenedor = (c) =>
    setContenedoresSel((prev) => ({ ...prev, [c]: !prev[c] }));

  const toggleTodosContenedores = () => {
    const todos = listaContenedores.every((c) => contenedoresSel[c]);
    const next = {};
    listaContenedores.forEach((c) => {
      next[c] = !todos;
    });
    setContenedoresSel(next);
  };

  const reporteFiltrado = useMemo(() => {
    if (!search) return reporte;
    const s = search.toLowerCase();
    return reporte.filter(
      (r) =>
        (r.SKU || "").toLowerCase().includes(s) ||
        (r.nombre || "").toLowerCase().includes(s)
    );
  }, [reporte, search]);

  const toggleSeleccion = (id) => {
    setSeleccionados((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleTodos = () => {
    const todosMarcados = reporteFiltrado.every((r) => seleccionados[r.idArticulo]);
    const nuevo = { ...seleccionados };
    reporteFiltrado.forEach((r) => {
      nuevo[r.idArticulo] = !todosMarcados;
    });
    setSeleccionados(nuevo);
  };

  const marcadosCount = reporteFiltrado.filter(
    (r) => seleccionados[r.idArticulo]
  ).length;

  function costearSeleccionados() {
    const aCostear = reporte.filter((r) => seleccionados[r.idArticulo]);
    if (aCostear.length === 0) {
      Swal.fire({
        icon: "info",
        title: "Sin selección",
        text: "Marca al menos un artículo para costear.",
      });
      return;
    }
    Swal.fire({
      title: `¿Costear ${aCostear.length} artículo(s)?`,
      text: "Se actualizará el costo (costoMX) con el promedio ponderado.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, costear",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;
      setValidaBoton(false);
      const headers = {
        Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
      };
      const reqs = aCostear.map((r) =>
        axios.patch(
          `${URL_ARTICULOS}EditarCostoMX/${r.idArticulo}`,
          { costoMX: Math.round((Number(r.costoMXNuevo) || 0) * 100) / 100 },
          { headers }
        )
      );
      Promise.all(reqs)
        .then(() => {
          axios.post(
            URL_LOGS,
            {
              tipo: "Costear Articulos",
              detalle: `${aCostear.length} artículo(s) costeados`,
              user: user.id,
            },
            { headers }
          );
          Swal.fire("¡Listo!", "Artículos costeados con éxito", "success");
          setTimeout(() => window.location.reload(), 1200);
        })
        .catch((error) => {
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Something went wrong!",
            footer: `${error?.response?.data || ""}`,
          });
          console.log(error);
          setValidaBoton(true);
        });
    });
  }

  function dataPlana() {
    return reporteFiltrado.map((r) => ({
      SKU: r.SKU,
      Articulo: r.nombre,
      Existencia: r.existencia,
      CostoActual: Number(r.costoMXActual) || 0,
      CantidadNueva: r.cantidadNueva,
      CostoEntrada: Number(r.costoEntrada) || 0,
      CostoNuevo: Number(r.costoMXNuevo) || 0,
    }));
  }

  function PDFTabla() {
    const data = dataPlana().map((a) => [
      a.SKU,
      a.Articulo,
      new Intl.NumberFormat("en-US").format(a.Existencia),
      monedaMXN(a.CostoActual),
      new Intl.NumberFormat("en-US").format(a.CantidadNueva),
      monedaMXN(a.CostoEntrada),
      monedaMXN(a.CostoNuevo),
    ]);
    const doc = new jsPDF("landscape", "mm", "a4", true);
    var img = new Image();
    img.src = process.env.PUBLIC_URL + "Logo-Lynqro-PDF.png";
    doc.addImage(img, "png", 230, 8, 45, 15);
    var img2 = new Image();
    img2.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img2, "png", 125, 5, 30, 30);
    doc.text(`Reporte de Costos por Artículo`, 20, 20);
    doc.text(`${selectedFechaInicio} - ${selectedFechaFin}`, 20, 28);
    doc.autoTable({
      head: [
        [
          "SKU",
          "Artículo",
          "Existencia",
          "Costo Actual",
          "Cant. Nueva",
          "Costo Entrada",
          "Costo Nuevo",
        ],
      ],
      body: data,
      startY: 34,
    });
    doc.save(`ReporteCostosArticulo.pdf`);
  }

  function excel() {
    const option = {};
    option.fileName = "ReporteCostosArticulo";
    option.datas = [
      {
        sheetData: dataPlana(),
        sheetName: "CostosArticulo",
        sheetFilter: [
          "SKU",
          "Articulo",
          "Existencia",
          "CostoActual",
          "CantidadNueva",
          "CostoEntrada",
          "CostoNuevo",
        ],
        sheetHeader: [
          "SKU",
          "Articulo",
          "Existencia",
          "Costo Actual",
          "Cantidad Nueva",
          "Costo Entrada",
          "Costo Nuevo",
        ],
      },
    ];
    const toExcel = new ExportJsonExcel(option);
    toExcel.saveExcel();
  }

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_compras ? (
        <div className="card col-12">
          <Row>
            <Col md={6}>
              <Button
                size="sm"
                href="/MenuCompras"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
            <Col sm={6}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFTabla}>
                  PDF <i class="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i class="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
          <h3 align="center">Reporte de Costos por Artículo</h3>
          <Row className="align-items-end">
            <Col md={2}>
              <Label className="mr-sm-2">Fecha Inicio</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaInicio}
                onChange={(e) => setSelectedFechaInicio(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label className="mr-sm-2">Fecha Fin</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaFin}
                onChange={(e) => setSelectedFechaFin(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label className="mr-sm-2">% Adicional</Label>
              <Input
                bsSize="sm"
                type="number"
                step="any"
                min="0"
                value={porcentaje}
                onChange={(e) => setPorcentaje(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Label className="mr-sm-2">Buscar</Label>
              <Input
                bsSize="sm"
                type="text"
                placeholder="SKU o artículo"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col md={3} className="text-right">
              {user.compras_costear ? (
                validaBoton ? (
                  <Button
                    size="sm"
                    color="success"
                    onClick={costearSeleccionados}
                  >
                    <i class="fas fa-tags"></i> Costear seleccionados ({marcadosCount})
                  </Button>
                ) : (
                  <Button size="sm" color="success" disabled>
                    Costeando...
                  </Button>
                )
              ) : undefined}
            </Col>
          </Row>
          <br />
          {/* Selección de contenedores que se promedian */}
          {listaContenedores.length > 0 ? (
            <div
              className="card"
              style={{ padding: 10, marginBottom: 12, background: "#f8fafc" }}
            >
              <div className="d-flex align-items-center mb-1">
                <strong style={{ marginRight: 12 }}>
                  Contenedores a promediar
                </strong>
                <Button size="sm" color="link" onClick={toggleTodosContenedores}>
                  {listaContenedores.every((c) => contenedoresSel[c])
                    ? "Quitar todos"
                    : "Seleccionar todos"}
                </Button>
              </div>
              <div className="d-flex flex-wrap">
                {listaContenedores.map((c) => (
                  <label
                    key={c}
                    className="mr-3 mb-1"
                    style={{ cursor: "pointer", fontSize: 13 }}
                  >
                    <Input
                      type="checkbox"
                      checked={!!contenedoresSel[c]}
                      onChange={() => toggleContenedor(c)}
                      style={{ position: "static", margin: "0 6px 0 0" }}
                    />
                    {c}
                  </label>
                ))}
              </div>
            </div>
          ) : null}
          {reporteFiltrado.length === 0 ? (
            <h5 align="center" style={{ color: "#64748b" }}>
              Sin artículos con pagos/contenedor en el periodo seleccionado
            </h5>
          ) : (
            <Table size="sm" striped borderless className="table-responsive-xl">
              <thead>
                <tr>
                  <th>
                    <Input
                      type="checkbox"
                      checked={
                        reporteFiltrado.length > 0 &&
                        reporteFiltrado.every((r) => seleccionados[r.idArticulo])
                      }
                      onChange={toggleTodos}
                      style={{ position: "static", margin: 0 }}
                    />
                  </th>
                  <th>SKU</th>
                  <th>Artículo</th>
                  <th className="text-right">Existencia</th>
                  <th className="text-right">Costo Actual</th>
                  <th className="text-right">Cant. Nueva</th>
                  <th className="text-right">Costo Entrada</th>
                  <th className="text-right">Costo Nuevo</th>
                </tr>
              </thead>
              <tbody>
                {reporteFiltrado.map((r) => {
                  const sube = (Number(r.costoMXNuevo) || 0) > (Number(r.costoMXActual) || 0);
                  return (
                    <tr key={r.idArticulo}>
                      <td>
                        <Input
                          type="checkbox"
                          checked={!!seleccionados[r.idArticulo]}
                          onChange={() => toggleSeleccion(r.idArticulo)}
                          style={{ position: "static", margin: 0 }}
                        />
                      </td>
                      <td>{r.SKU}</td>
                      <td>{r.nombre}</td>
                      <td className="text-right">
                        {new Intl.NumberFormat("en-US").format(r.existencia)}
                      </td>
                      <td className="text-right">{monedaMXN(r.costoMXActual)}</td>
                      <td className="text-right">
                        {new Intl.NumberFormat("en-US").format(r.cantidadNueva)}
                      </td>
                      <td className="text-right">{monedaMXN(r.costoEntrada)}</td>
                      <td className="text-right" style={{ fontWeight: 700 }}>
                        {monedaMXN(r.costoMXNuevo)}{" "}
                        <Badge color={sube ? "danger" : "success"} pill>
                          {sube ? "↑" : "↓"}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
      ) : undefined}
      {loader}
    </>
  );
}

export default ReporteCostosArticulo;
